/**
 * @file groupRowsBy.ts
 * @brief Group flat table rows by a stable key for collapsible employee sections.
 *
 * @author DES
 * @date 07-07-2026
 */

export interface TableRowGroup<TData> {
  key: string;
  label: string;
  rows: TData[];
  /** Optional nested sub-groups (e.g. users under Personal). */
  children?: TableRowGroup<TData>[];
  avatarSrc?: string | null;
  avatarGender?: string | null;
  avatarInitials?: string;
  /**
   * Server-backed group (01-10-2026, company Sessions): the real number of
   * rows, when `rows` holds only those loaded so far. Load more then asks
   * the caller (`onGroupLoadMore`) instead of revealing rows already here.
   */
  total?: number;
  /** Server-backed group: its rows are being fetched. */
  loading?: boolean;
  /**
   * Server-backed group: its last fetch failed (W33, 30-10-2026). The table
   * shows "Could not load these …" with Retry instead of "Showing 0 of N".
   */
  error?: boolean;
  /** Server-backed group: fetch its rows again after an error. */
  onRetry?: () => void;
}

/**
 * What closes an open leaf group under its rows (W33, 30-10-2026): the error
 * row when a server-backed fetch failed, Load more while rows remain, else
 * nothing. A failed group never offers "Load N more" over 0 rows.
 */
export function groupFooterKind<TData>(
  group: Pick<TableRowGroup<TData>, "total" | "loading" | "error">,
  remaining: number,
): "error" | "load-more" | null {
  const serverBacked = group.total !== undefined;
  if (serverBacked && group.loading) return null;
  if (serverBacked && group.error) return "error";
  return remaining > 0 ? "load-more" : null;
}

export function collectGroupKeys<TData>(
  groups: TableRowGroup<TData>[],
): string[] {
  const keys: string[] = [];
  for (const group of groups) {
    keys.push(group.key);
    if (group.children?.length) {
      keys.push(...collectGroupKeys(group.children));
    }
  }
  return keys;
}

export function groupRowsBy<TData>(
  rows: TData[],
  keyFn: (row: TData) => string,
  metaFn: (row: TData) => Omit<TableRowGroup<TData>, "key" | "rows">,
): TableRowGroup<TData>[] {
  const map = new Map<string, TData[]>();

  for (const row of rows) {
    const key = keyFn(row);
    const bucket = map.get(key);
    if (bucket) {
      bucket.push(row);
    } else {
      map.set(key, [row]);
    }
  }

  return Array.from(map.entries())
    .map(([key, groupRows]) => ({
      key,
      rows: groupRows,
      ...metaFn(groupRows[0]!),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

type GroupMeta<TData> = Omit<
  TableRowGroup<TData>,
  "key" | "rows" | "children"
>;

/** Company (or Personal) top-level groups, each with nested user sub-groups. */
export function groupByCompanyThenUser<TData>(
  rows: TData[],
  companyKeyFn: (row: TData) => string,
  companyMetaFn: (row: TData) => GroupMeta<TData>,
  userKeyFn: (row: TData) => string,
  userMetaFn: (row: TData) => GroupMeta<TData>,
): TableRowGroup<TData>[] {
  const companyGroups = groupRowsBy(rows, companyKeyFn, companyMetaFn);

  return companyGroups.map((group) => {
    const children = groupRowsBy(group.rows, userKeyFn, userMetaFn).map(
      (child) => ({
        ...child,
        key: `${group.key}/${child.key}`,
      }),
    );

    return {
      ...group,
      rows: [],
      children,
    };
  });
}
